import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { BaseDomainModel } from '../models/base/base-domain-model';
import { CompanyDomainModel } from './company-domain-model';
import { InsightsRepository } from '../repositories/insights/insights-repository';
import type { FetchStrategy } from '../repositories/base/fetch-strategy';
import { HighGuideInsightCollectionResult } from '../models/guide/dto/high-guide-insight-collection-result';
import { HighGuideInsights } from '../models/guide/dto/high-guide-insights';
import { HighGuideRecentRecommendations } from '../models/guide/dto/high-guide-recent-recommendations';
import { HighGuideTrendingBrand } from '../models/guide/dto/high-guide-trending-brand';
import { LocationDomainModel } from './location-domain-model';
import { MediaManagerDomainModel } from './media-manager/media-manager-domain-model';
import { ProductDomainModel } from './product-domain-model';
import { ProductRepository } from '../repositories/product/product-repository';
import { Asset } from '../models/image/dto/asset';
import { HighGuideInsightsDebugUtils } from '../utils/high-guide-insights-debug-utils';
import { VariantMediaAssetUtils } from '../utils/variant-media-asset-utils';
import { DistinctUtils } from '../utils/distinct-utils';
import { exists } from '../functions/exists';
import { AssetUtils } from '../utils/asset-utils';
import type { Variant } from '../models/product/dto/variant';
import type { CompanyConfiguration } from '../models/company/dto/company-configuration';
import type { VariantType } from '../models/utils/dto/variant-type-definition';

/**
 * The HighGuide Insights rollup behind the three home page widgets - What's Hot, Purchasing Insights and Recently
 * Recommended.
 *
 * Deliberately separate from `ProductSignalsDomainModel`. That one exists to relay raw signal streams into the
 * embedded HighGuide iframe; this is a precomputed read on its own fetch cadence, and folding it in would tie the
 * iframe relay's lifecycle to a surface it has nothing to do with.
 *
 * The payload is company-wide but carries only variant ids, while the product catalogue is location-scoped. Ids
 * the catalogue does not hold are backfilled into a page-local lookup - see `insightsVariantLookup$`.
 *
 * `insights$` is `null` while loading, per the house convention, and resets to `null` on every location change. A
 * failed read resolves to `null` rather than propagating, so the widgets drop back to their empty states instead of
 * hanging on a shimmer forever.
 */
// Provided by Logged In Scope
@Injectable()
export class HighGuideInsightsDomainModel extends BaseDomainModel {
  constructor(
    protected insightsRepository: InsightsRepository,
    protected companyDomainModel: CompanyDomainModel,
    protected locationDomainModel: LocationDomainModel,
    protected mediaManagerDomainModel: MediaManagerDomainModel,
    protected productDomainModel: ProductDomainModel,
    protected productRepository: ProductRepository
  ) {
    super();
    this.fetchInsightsForLocation();
  }

  private static readonly maxVariantIdsPerRequest = 100;

  protected readonly _insights = new BehaviorSubject<HighGuideInsights | null>(null);
  public readonly insights$ = this._insights as Observable<HighGuideInsights | null>;

  /**
   * The pool is otherwise fetched once per location, but the embedded HighGuide iframe writes reviews straight to the
   * API behind the dashboard's back. Without a refetch, a review left in HighGuide is missing from Recently
   * Recommended until a page load. Home asks for one on every visit, and again on each review pushed while it is open.
   */
  private readonly _refreshInsights = new Subject<void>();
  private readonly refreshInsights$ = this._refreshInsights as Observable<void>;
  connectToRefreshInsights = () => this._refreshInsights.next();

  public readonly trendingBrands$: Observable<HighGuideTrendingBrand[] | null> = this.insights$.pipe(
    map(insights => insights?.trendingBrands ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly recentRecommendations$: Observable<HighGuideRecentRecommendations | null> = this.insights$.pipe(
    map(insights => insights?.recentRecommendations ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly insightCollectionResults$: Observable<HighGuideInsightCollectionResult[] | null> =
    this.insights$.pipe(
      map(insights => insights?.insightCollections ?? null),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  /**
   * The window the backend actually used, for the section copy to quote. `null` until the first payload lands, so
   * the copy renders a length it can stand behind rather than a hardcoded guess.
   */
  public readonly reportingWindowDays$: Observable<number | null> = this.insights$.pipe(
    map(insights => insights?.windowDays ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Whether this company has the HighGuide Pro feature.
   *
   * Hardcoded on. Purchasing Insights is not behind a paywall yet - the Pro chip on the section is a forward signal
   * that it is planned to be - so every company sees the results for now. There is no tier or entitlement anywhere
   * in the client to read instead, and inventing one speculatively would be worse than this single obvious seam.
   *
   * The locked treatment and upsell banner are built and wired behind this stream; flipping this line to
   * `of(false)` is the whole of exercising them. Note the lock is a CSS blur over the real results, which is a
   * visual teaser and not a control - when the paywall is real, the data must be withheld by the API and the
   * teaser rendered from static imagery instead.
   *
   * TODO: replace with the real entitlement once the API carries one.
   */
  public readonly isHighGuidePro$: Observable<boolean> = of(true);

  /**
   * Every variant the payload names, across both the recommendations and every collection's products.
   *
   * Deduped and fetched once for the whole page rather than per widget - the two surfaces overlap heavily, and the
   * asset read is by far the most expensive thing either of them does.
   */
  private readonly referencedVariantIds$: Observable<string[] | null> = this.insights$.pipe(
    map(insights => {
      if (!insights) return null;
      const variantIds = new Set<string>();
      (insights.recentRecommendations?.recommendations ?? []).forEach(signal => variantIds.add(signal?.variantId));
      (insights.insightCollections ?? []).forEach(collection => {
        (collection?.products ?? []).forEach(product => variantIds.add(product?.variantId));
      });
      return Array.from(variantIds).filter(variantId => exists(variantId));
    }),
    distinctUntilChanged((previous, current) => previous?.join(',') === current?.join(',')),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Referenced variants the location catalogue does not hold - carried at another location only, or discontinued
   * here - fetched by id across the company. `null` until resolved, so cells shimmer instead of flashing empty.
   *
   * Only an unloaded pool or catalogue blanks it. A refresh that names a new variant keeps the previous lookup up
   * until the new one lands: blanking it there nulls `insightsVariantLookup$`, which put the whole Recently
   * Recommended row back on its shimmer every time a teammate reviewed a product stocked elsewhere.
   *
   * The dedup matters: the location lookup re-emits the same mutable Map on every product update, and without it
   * each of those would refetch. A failed read resolves to an empty map rather than a toast, as elsewhere here.
   */
  private readonly fallbackVariantLookup$: Observable<Map<string, Variant> | null> = combineLatest([
    this.referencedVariantIds$,
    this.productDomainModel.currentLocationVariantLookup$
  ]).pipe(
    map(([variantIds, variantLookup]) => {
      if (!variantIds || !variantLookup) return null;
      return variantIds.filter(variantId => !variantLookup.has(variantId)).sort();
    }),
    distinctUntilChanged(DistinctUtils.distinctStringLists),
    switchMap(missingVariantIds => this.fetchFallbackVariants$(missingVariantIds)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * The location lookup overlaid with the backfilled variants, location winning on collision. Every insights
   * surface reads this instead of `currentLocationVariantLookup$`.
   *
   * The fallback variants live only here. They must never reach the global product pool, where cross-location
   * products would leak into menus, product tables and smart filters. A new Map is built rather than mutating the
   * location lookup, which is shared with the rest of the app.
   */
  public readonly insightsVariantLookup$: Observable<Map<string, Variant> | null> = combineLatest([
    this.productDomainModel.currentLocationVariantLookup$,
    this.fallbackVariantLookup$
  ]).pipe(
    map(([variantLookup, fallbackLookup]) => {
      if (!variantLookup || !fallbackLookup) return null;
      const merged = new Map<string, Variant>(fallbackLookup);
      variantLookup.forEach((variant, variantId) => merged.set(variantId, variant));
      return merged;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * One thumbnail per variant, picked the way the variant modal's header picks its own - through
   * `getFirstDisplayableVariantMediaAsset`, so the home page never shows a product an image the editor considers
   * disabled or hidden.
   *
   * A variant with nothing displayable is simply absent from the map; the widgets read that as "no image" and drop
   * the element rather than drawing an empty frame. A failed read resolves to an empty map for the same reason -
   * missing thumbnails are not worth a toast on a passive summary.
   */
  /**
   * `null` while the pool is loading, per the house convention, so consumers can hold a widget on its shimmer until
   * the imagery is actually resolved rather than drawing rows and popping images in afterwards.
   *
   * Only an unloaded pool blanks it - first load and a location switch both null `insights$` first. A refresh keeps
   * the previous map up while the next fetch runs: those thumbnails are still right for every card already drawn,
   * and blanking them stripped every image off the page and sent Purchasing Insights back to its shimmer whenever a
   * refresh named a new variant. Each fetch is the full set, so a cancelled one cannot leave the map short.
   */
  public readonly variantThumbnails$: Observable<Map<string, Asset> | null> = this.referencedVariantIds$.pipe(
    switchMap(variantIds => (variantIds ? this.getVariantThumbnails$(variantIds) : of(null))),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * The same thumbnail resolution for an arbitrary set of variants, so the empty states can borrow imagery from the
   * catalogue rather than from a payload that by definition has none.
   *
   * The API does the ranking, exactly as it does for HighGuide and the menus: each request carries the company's
   * configured media category hierarchy for that variant type, so company and BPD imagery are weighed against each
   * other by the configured order rather than the client preferring one library wholesale.
   */
  public getVariantThumbnails$(variantIds: string[]): Observable<Map<string, Asset>> {
    if (!variantIds?.length) return of(new Map<string, Asset>());
    return combineLatest([this.insightsVariantLookup$, this.companyDomainModel.companyConfiguration$]).pipe(
      switchMap(([variantLookup, companyConfiguration]) => {
        if (!variantLookup) return of(new Map<string, Asset>());
        const variants = variantIds.map(variantId => variantLookup.get(variantId)).filter(variant => exists(variant));
        const requests = this.buildRankedAssetRequests$(variants, companyConfiguration);
        if (!requests.length) return of(new Map<string, Asset>());
        return forkJoin(requests).pipe(
          map(assetMaps => AssetUtils.flattenVariantMaps(assetMaps)),
          map(assetsByVariantId => this.pickThumbnails(variantIds, variantLookup, assetsByVariantId)),
          catchError(() => of(new Map<string, Asset>()))
        );
      })
    );
  }

  /**
   * One request per variant type per batch of 50. The asset type sort order is configured per variant type, so
   * variants that resolve to different orders cannot share a request.
   *
   * One asset per variant, because this grid draws one - the same count HighGuide and the menus ask for. Asking
   * for spare candidates to search through would be second-guessing the hierarchy the request just supplied.
   */
  private buildRankedAssetRequests$(
    variants: Variant[],
    companyConfiguration: CompanyConfiguration
  ): Observable<Map<string, Asset[]>>[] {
    const idsByVariantType = new Map<VariantType, string[]>();
    variants.forEach(variant => {
      const ids = idsByVariantType.get(variant?.variantType) ?? [];
      ids.push(variant?.id);
      idsByVariantType.set(variant?.variantType, ids);
    });
    return [...idsByVariantType.entries()].flatMap(([variantType, ids]) => {
      const sortOrder = companyConfiguration?.getAssetTypeSortOrderForProductImages(variantType)?.join(',') ?? '';
      return AssetUtils.batchVariantIds(ids)
        .filter(batch => batch?.length > 0)
        .map(batch => this.mediaManagerDomainModel.getVariantAssets$(batch, false, 1, sortOrder));
    });
  }

  /**
   * The API ranked and returned the winner, so the checks below are a gate on that one asset rather than a search
   * for a substitute - re-sorting or reaching further down the library would undo the hierarchy the request just
   * supplied.
   *
   * Two things the image service cannot know about gate it. `disabledAssets` is dashboard-only, and an image a
   * merchandiser hid on the Media tab must not resurface here. Video is excluded because `app-asset` plays a clip
   * as an autoplaying `<video>`, which paints a black box at 3rem in this grid. A variant failing either draws no
   * thumbnail, which is what HighGuide would show too.
   */
  private pickThumbnails(
    variantIds: string[],
    variantLookup: Map<string, Variant>,
    assetsByVariantId: Map<string, Asset[]>
  ): Map<string, Asset> {
    const thumbnails = new Map<string, Asset>();
    variantIds.forEach(variantId => {
      const variant = variantLookup.get(variantId);
      if (!variant) return;
      const candidates = assetsByVariantId?.get(variantId) ?? [];
      const thumbnail = candidates.find(asset => {
        const identifier = VariantMediaAssetUtils.getVariantMediaAssetIdentifier(asset);
        return asset?.isImage() && !variant.disabledAssets?.includes(identifier);
      });
      if (exists(thumbnail)) thumbnails.set(variantId, thumbnail);
    });
    return thumbnails;
  }

  /** Chunked because the API rejects more than 100 `VariantIds` in one request. */
  private fetchFallbackVariants$(variantIds: string[] | null): Observable<Map<string, Variant> | null> {
    if (!variantIds) return of(null);
    if (!variantIds.length) return of(new Map<string, Variant>());
    return this.locationDomainModel.locationId$.pipe(
      take(1),
      switchMap(locationId => {
        const requests = variantIds
          .chunkedList(HighGuideInsightsDomainModel.maxVariantIdsPerRequest)
          .map(chunk => this.productRepository.getCompanyProductsByVariantIds$(locationId, chunk));
        return forkJoin(requests);
      }),
      map(productChunks => {
        const fallbackLookup = new Map<string, Variant>();
        productChunks.flat().forEach(product => {
          (product?.variants ?? []).forEach(variant => fallbackLookup.set(variant?.id, variant));
        });
        return fallbackLookup;
      }),
      catchError(() => of(new Map<string, Variant>()))
    );
  }

  /**
   * cache-then-fresh by default: insights reload wholesale on a location switch, and `fetchInsightsForLocation`
   * blanks the pool first, so the cached emission fills widgets already showing their loading state. A refresh reads
   * fresh-only, because the pool is still on screen and the cache can only hold what it already shows.
   */
  public getInsights$(strategy: FetchStrategy = 'cache-then-fresh'): Observable<HighGuideInsights> {
    return combineLatest([this.companyDomainModel.companyId$, this.locationDomainModel.locationId$]).pipe(
      take(1),
      switchMap(([companyId, locationId]) => {
        return this.insightsRepository.getInsights$(companyId, locationId, undefined, { strategy });
      }),
      // Development only, and inert in production builds. See HighGuideInsightsDebugUtils.
      map(insights => HighGuideInsightsDebugUtils.emptyRequestedBranches(insights)),
      tap(insights => this._insights.next(insights))
    );
  }

  /**
   * Insights are location scoped, so the pool is cleared and refetched on every location change.
   *
   * A failure resolves to `null` here rather than surfacing a toast: this is a passive home page summary, and the
   * three widgets already have empty states that read as "nothing to show yet". Swallowing it also keeps the error
   * from terminating `locationId$`, which would stop insights refetching for the rest of the session. A failed
   * refresh never reaches `_insights`, so the widgets keep the pool they were already showing.
   *
   * Refreshes ride the same `switchMap` as the location fetch rather than firing alongside it, so one still in flight
   * across a location switch is cancelled instead of landing the old location's insights over the new one's. One
   * requested before the location fetch subscribes is dropped, which is correct: that fetch is already a fresh read.
   */
  private fetchInsightsForLocation(): void {
    this.locationDomainModel.locationId$
      .pipe(
        // Clearing before the dedup is safe because `locationId$` already ends in its own `distinctUntilChanged`,
        // so it never emits the same id twice and every emission that blanks the pool also reaches the refetch
        // below. The dedup here is belt and braces against that changing upstream; if it is ever the one doing the
        // filtering, this `tap` has to move below it or the widgets will sit on a null pool forever.
        tap(_ => this._insights.next(null)),
        distinctUntilChanged(),
        debounceTime(100),
        switchMap(locationId => {
          if (!locationId) return of(null);
          return this.refreshInsights$.pipe(
            startWith(undefined),
            map((_, index): FetchStrategy => (index === 0 ? 'cache-then-fresh' : 'fresh-only')),
            switchMap(strategy => this.getInsights$(strategy).pipe(catchError(() => of(null))))
          );
        })
      )
      .subscribeWhileAlive({ owner: this });
  }
}
